/**
 * Agora Usage Page
 * Track and monitor Agora session usage and costs
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { 
  Video, 
  RefreshCw, 
  DollarSign, 
  Clock, 
  Users,
  TrendingDown,
  Activity
} from 'lucide-react';

const AgoraUsagePage = () => {
  const { api } = useAdmin();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, sessionsRes] = await Promise.all([
        api.get('/api/admin/agora-stats'),
        api.get(`/api/admin/agora-sessions?page=${page}&page_size=20`)
      ]);
      
      setStats(statsRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setTotalPages(sessionsRes.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching Agora data:', error);
      toast.error('Failed to fetch Agora usage data');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="h-7 w-7 text-blue-500" />
            Agora Usage Dashboard
          </h1>
          <p className="text-gray-600">Monitor Agora video streaming costs and usage</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_sessions || 0}</p>
            </div>
            <Activity className="h-10 w-10 text-blue-500 opacity-50" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats?.active_sessions || 0} currently active
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Minutes Used</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_minutes?.toFixed(1) || 0}</p>
            </div>
            <Clock className="h-10 w-10 text-amber-500 opacity-50" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats?.today?.estimated_minutes?.toFixed(1) || 0} min today
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Estimated Cost</p>
              <p className="text-3xl font-bold text-emerald-600">${stats?.estimated_cost_usd?.toFixed(2) || '0.00'}</p>
            </div>
            <DollarSign className="h-10 w-10 text-emerald-500 opacity-50" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Based on ~$0.99/1000 min
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Multiplayer Sessions</p>
              <p className="text-3xl font-bold text-purple-600">{stats?.multiplayer_game_sessions || 0}</p>
            </div>
            <Users className="h-10 w-10 text-purple-500 opacity-50" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Agora used exclusively for multiplayer
          </p>
        </div>
      </div>

      {/* Cost Savings Note */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <TrendingDown className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-emerald-800">Cost Optimization Active</h3>
            <p className="text-sm text-emerald-700 mt-1">
              Agora is now used <strong>only for multiplayer gameplay sessions</strong> (4-player Spades). 
              Solo streams no longer consume Agora minutes. 
              {stats?.other_sessions > 0 && (
                <span className="text-amber-700"> Note: {stats.other_sessions} legacy sessions detected.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900">Session History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Channel</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Duration</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No Agora sessions recorded yet
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(session.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {session.user_email || session.user_id?.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">
                      {session.channel_name?.slice(0, 20)}...
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        session.session_type === 'multiplayer_game' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {session.session_type || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {session.role}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {session.estimated_minutes ? `${session.estimated_minutes.toFixed(1)} min` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        session.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {session.status === 'active' ? 'Active' : 'Ended'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgoraUsagePage;
